import type { EventPhaseKind } from "@/constants/eventPhases";
import {
  formatZonedDayKey,
  zonedDayKeyToInstant,
} from "@/Utils/Date/zonedTime";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function padDateUnit(value: number) {
  return String(value).padStart(2, "0");
}

const localNoonOf = (year: number, month: number, day: number) =>
  new Date(year, month - 1, day, 12, 0, 0, 0);

const dayKeyToLocalNoon = (dayKey: string) => {
  const [year, month, day] = dayKey.split("-").map(Number);
  return localNoonOf(year, month, day);
};

/**
 * Calendar day of a value, as a local Date at noon (safe to shift by days and
 * to format without landing on the neighbouring day).
 *
 * - a pure `YYYY-MM-DD` string IS a calendar day and is taken as is;
 * - a full timestamp (`2026-09-24T22:00:00.000Z`) is an absolute instant: its
 *   day is the one it falls on in `APP_TIMEZONE`, like the backend — never
 *   its UTC prefix (that is the day before for a Paris midnight);
 * - a `Date` object comes from the browser (date pickers, `new Date()`) and
 *   keeps its local day.
 */
export function normalizeDateValue(value: Date | string) {
  if (typeof value === "string") {
    if (ISO_DAY_PATTERN.test(value)) {
      return dayKeyToLocalNoon(value);
    }

    const instant = new Date(value);

    if (!Number.isNaN(instant.getTime())) {
      return dayKeyToLocalNoon(formatZonedDayKey(instant));
    }
  }

  const nextDate = new Date(value);
  nextDate.setHours(12, 0, 0, 0);
  return nextDate;
}

export function formatDateInputValue(value: Date) {
  const date = normalizeDateValue(value);

  return `${date.getFullYear()}-${padDateUnit(date.getMonth() + 1)}-${padDateUnit(date.getDate())}`;
}

/**
 * Calendar day (`YYYY-MM-DD`) an event date stands for, whatever its shape:
 * a day key as is, an API timestamp on its `APP_TIMEZONE` day, a `Date` on
 * the day it carries (see {@link normalizeDateValue}). Every day decision on
 * event dates goes through this one key, never through a raw local `Date`.
 */
export function toEventDayKey(value: Date | string) {
  return formatDateInputValue(normalizeDateValue(value));
}

/**
 * Wire format of an event date: midnight of its calendar day in
 * `APP_TIMEZONE`, the instant the API stores. A browser-local Date sent as
 * is lands on another day for a viewer twelve hours or more away from the
 * app's zone (Kiritimati local noon is the previous day in Paris), and every
 * save moved the event back one more day. This value reads back as the same
 * day key in any browser zone, so read -> save is the identity.
 */
export function serializeEventDate(value: Date | string) {
  return zonedDayKeyToInstant(toEventDayKey(value)).toISOString();
}

export function parseDateInputValue(value: string) {
  return normalizeDateValue(value);
}

export function addDaysToDate(value: Date | string, days: number) {
  const nextDate = normalizeDateValue(value);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

export function getCalendarDaySpan(startDate: Date, endDate: Date) {
  const start = normalizeDateValue(startDate);
  const end = normalizeDateValue(endDate);

  return Math.max(0, Math.round((end.getTime() - start.getTime()) / DAY_IN_MS));
}

export function formatDisplayDate(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(normalizeDateValue(value));
}

export function formatCompactDate(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  }).format(normalizeDateValue(value));
}

export function getFullDayEventRange(
  startValue: Date | string,
  endValue: Date | string,
) {
  const start = normalizeDateValue(startValue);
  const end = normalizeDateValue(endValue);

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

export interface EventDayWindowItem {
  section_date: string;
  window_start: string;
  window_end: string;
  /** Phase of the window — required by the API on every write. */
  kind: EventPhaseKind;
}

export function setTimeOnDate(
  value: Date | string,
  timeValue: string,
  dayOffset = 0,
) {
  const nextDate = normalizeDateValue(value);
  const [hours, minutes] = timeValue.split(":").map((part) => Number(part));

  nextDate.setHours(
    Number.isFinite(hours) ? hours : 0,
    Number.isFinite(minutes) ? minutes : 0,
    0,
    0,
  );
  nextDate.setDate(nextDate.getDate() + dayOffset);

  return nextDate;
}

export function buildEventWindowRanges(dayWindows: EventDayWindowItem[]) {
  return dayWindows.map((window) => {
    const start = setTimeOnDate(window.section_date, window.window_start);
    const crossesMidnight = window.window_end <= window.window_start;
    const end = setTimeOnDate(
      window.section_date,
      window.window_end,
      crossesMidnight ? 1 : 0,
    );

    return { end, start };
  });
}

export function formatTimeInputValue(value: Date | string) {
  const date = new Date(value);

  return `${padDateUnit(date.getHours())}:${padDateUnit(date.getMinutes())}`;
}

export function formatDisplayTime(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

/**
 * Wall-clock time (`HH:MM`, 24h) of an absolute instant in the viewer's local
 * timezone. Timestamps are stored as UTC instants, so the raw ISO `HH:MM` is
 * offset from the local time the role was authored in — this renders the
 * instant back to local wall-clock, like the rest of the planning UI.
 */
export function formatLocalTime(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

/** Local calendar day (`YYYY-MM-DD`) of an absolute instant. */
export function formatLocalDayKey(value: Date | string) {
  const date = new Date(value);

  return `${date.getFullYear()}-${padDateUnit(date.getMonth() + 1)}-${padDateUnit(date.getDate())}`;
}
